import {
  ArrowLeft,
  Code2,
  Cpu,
  GitCompare,
  GitFork,
  Layers,
  Plus,
  Settings2,
} from 'lucide-react';
import { useState } from 'react';
import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import { Link, useLoaderData } from 'react-router';
import DecisionPoint from '~/components/spec/DecisionPoint';
import FeatureCard from '~/components/spec/FeatureCard';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { getRelatedSpecs, getSpecBySlug } from '~/lib/db/queries/specs';
import { getImplementationsForSpec } from '~/lib/implementation';
import { CATEGORY_LABELS } from '~/lib/utils';

export const meta: MetaFunction<typeof loader> = ({ loaderData }) => {
  if (!loaderData) return [{ title: 'Spec — Specly' }];
  const { spec } = loaderData;
  return [
    { title: `${spec.name} — Specly` },
    { name: 'description', content: spec.description },
    { property: 'og:title', content: `${spec.name} — Specly` },
    { property: 'og:description', content: spec.description },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: `https://specly.danielarrizza.com/specs/${spec.slug}` },
    {
      property: 'og:image',
      content: `https://specly.danielarrizza.com/specs/${spec.slug}/og.png`,
    },
  ];
};

export async function loader({ params }: LoaderFunctionArgs) {
  const spec = await getSpecBySlug(params.slug!);
  if (!spec) throw new Response('Not Found', { status: 404 });
  const relatedSpecs = await getRelatedSpecs(spec.id, spec.category, 3);
  const implementationCount = getImplementationsForSpec(spec.slug).length;
  return { spec, relatedSpecs, implementationCount };
}

const MAX_VISIBLE_DECISIONS = 6;

export default function SpecViewer() {
  const { spec, relatedSpecs, implementationCount } =
    useLoaderData<typeof loader>();
  const [showAllDecisions, setShowAllDecisions] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);

  const allDecisionPoints = spec.specFeatures.flatMap((sf) =>
    sf.definition.decisionPoints.map((dp) => ({
      ...dp,
      featureName: sf.feature.name,
    })),
  );

  const visibleDecisions = showAllDecisions
    ? allDecisionPoints
    : allDecisionPoints.slice(0, MAX_VISIBLE_DECISIONS);
  const hiddenCount = allDecisionPoints.length - MAX_VISIBLE_DECISIONS;

  function handleCopyEmbed() {
    const snippet = `<iframe src="https://specly.danielarrizza.com/specs/${spec.slug}/embed" width="100%" height="400" frameborder="0"></iframe>`;
    navigator.clipboard.writeText(snippet);
    setEmbedCopied(true);
    setTimeout(() => setEmbedCopied(false), 2000);
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="size-4" />
          All specs
        </Link>

        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm text-muted-foreground">
                {CATEGORY_LABELS[spec.category] ?? spec.category}
              </span>
              {spec.isOfficial && <Badge variant="secondary">Official</Badge>}
            </div>
            <h1 className="text-3xl font-bold">{spec.name}</h1>
            <p className="mt-2 text-muted-foreground max-w-2xl">
              {spec.description}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
            <Button
              variant="outline"
              size="sm"
              render={<Link to={`/compare?a=${spec.slug}`} />}
            >
              <GitCompare data-icon="inline-start" />
              Compare
            </Button>
            <Button
              variant="outline"
              size="sm"
              render={<Link to={`/specs/${spec.slug}/build`} />}
            >
              <GitFork data-icon="inline-start" />
              Remix
            </Button>
            <Button
              variant="outline"
              size="sm"
              render={<Link to={`/compose?sources=${spec.slug}`} />}
            >
              <Plus data-icon="inline-start" />
              Add to compose
            </Button>
            <Button
              variant="outline"
              size="sm"
              render={<Link to={`/specs/${spec.slug}/tech`} />}
            >
              <Cpu data-icon="inline-start" />
              Tech stack
            </Button>
            {implementationCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                render={<Link to={`/specs/${spec.slug}/implementations`} />}
              >
                <Layers data-icon="inline-start" />
                Implementations ({implementationCount})
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleCopyEmbed}>
              <Code2 data-icon="inline-start" />
              {embedCopied ? 'Copied!' : 'Embed'}
            </Button>
            <Button
              size="sm"
              render={<Link to={`/specs/${spec.slug}/build`} />}
            >
              <Settings2 data-icon="inline-start" />
              Build this
            </Button>
          </div>
        </div>
      </div>

      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">
          Features
          <span className="ml-2 text-sm font-normal text-muted-foreground">
            ({spec.specFeatures.length})
          </span>
        </h2>
      </div>

      <div className="flex flex-col gap-3">
        {spec.specFeatures.map((sf) => (
          <FeatureCard
            key={sf.featureId}
            feature={{ ...sf.feature, definition: sf.definition }}
            isRequired={sf.isRequired}
          />
        ))}
      </div>

      {allDecisionPoints.length > 0 && (
        <div className="mt-10">
          <h2 className="text-lg font-semibold mb-4">Key Decisions</h2>
          <div className="flex flex-col gap-3">
            {visibleDecisions.map((dp, i) => (
              <div key={i}>
                <p className="text-xs text-muted-foreground mb-1 font-medium">
                  {dp.featureName}
                </p>
                <DecisionPoint point={dp} />
              </div>
            ))}
          </div>
          {!showAllDecisions && hiddenCount > 0 && (
            <Button
              variant="link"
              size="sm"
              className="mt-4 p-0 h-auto"
              onClick={() => setShowAllDecisions(true)}
            >
              Show {hiddenCount} more
            </Button>
          )}
        </div>
      )}

      {relatedSpecs.length > 0 && (
        <div className="mt-10">
          <h2 className="text-lg font-semibold mb-4">Related specs</h2>
          <div className="flex flex-wrap gap-3">
            {relatedSpecs.map((related) => (
              <Link
                key={related.id}
                to={`/specs/${related.slug}`}
                className="flex flex-col px-4 py-3 rounded-xl border border-border bg-card hover:border-primary/40 hover:bg-primary/5 transition-colors"
              >
                <span className="font-medium text-sm">{related.name}</span>
                <span className="text-xs text-muted-foreground mt-0.5">
                  {CATEGORY_LABELS[related.category] ?? related.category}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
